import React, {
  memo,
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDetectScroll } from '@saviynt/common';
import {
  Box,
  Button,
  FooterActionBar,
  Icon,
  InlineMessage,
  InputField,
  Loader,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';

import { MIN_COMMENT_LENGTH } from '../../../../../shared/constants';
import { useInvertedLocalize } from '../../../../../shared/hooks';
import { useHandleMitigatingControl } from '../../../../Details/hooks';

import './ViolationsRemoveMc.css';

interface ViolationsRemoveMcProps {
  sodKey: string;
  isOpen: boolean;
  isLoading: boolean;
  headerIcon: ReactElement;
  mcKeys?: number[];
  mitigatingControlCount?: number;
  handleSumbit: (
    comment: string,
    config: { mitigatingControlKeys?: number[]; violationId?: number }
  ) => void;
  handleModalClose: () => void;
  isDisabled?: boolean;
}

/**
 * ViolationsRemoveMc
 */
function ViolationsRemoveMc({
  sodKey,
  isOpen,
  isDisabled,
  isLoading,
  headerIcon,
  mcKeys,
  mitigatingControlCount,
  handleSumbit,
  handleModalClose,
}: ViolationsRemoveMcProps) {
  const l = useInvertedLocalize();
  const sectionRef = useRef(null);
  const { isScrolled: sectionRefIsScrolled }: { isScrolled: boolean } =
    useDetectScroll(null, sectionRef);

  const [commentValue, setCommentValue] = useState('');
  const [isCommentCritical, setIsCommentCritical] = useState(false);
  const [isLoadingMc, setIsLoadingMc] = useState(Boolean(!mcKeys));
  const [isErrorMc, setIsErrorMc] = useState(false);

  const { loadMitigateRow, mitigatingControlRows } = useHandleMitigatingControl(
    { isCacheDisabled: true }
  );

  useEffect(() => {
    if (sodKey && sodKey.length > 0 && isLoadingMc) {
      const requestData = {
        id: parseInt(sodKey, 10),
        violationId: sodKey,
      };

      setIsErrorMc(false);

      loadMitigateRow(requestData)
        .catch(() => setIsErrorMc(true))
        .finally(() => setIsLoadingMc(false));
    }
  }, [loadMitigateRow, sodKey, isLoadingMc]);

  // Constants
  const isSubmitButtonEnable = useMemo(
    () => commentValue.trim().length >= MIN_COMMENT_LENGTH,
    [commentValue]
  );
  const inSingleMc = useMemo(
    () =>
      Boolean(
        mcKeys &&
          mcKeys.length === 1 &&
          mitigatingControlCount &&
          mitigatingControlCount > 1
      ),
    [mcKeys, mitigatingControlCount]
  );

  const renderBody = () => {
    if (isLoadingMc) {
      return (
        <Box tag='div' className='Sod-MitigateControlsEndDate-loader'>
          <Loader kind='dots' size='small' format='inline' color='brand' />
        </Box>
      );
    }

    if (isErrorMc) {
      return (
        <Box tag='div' className='Sod-MitigateControlsEndDate-errorWrapper'>
          <Typography kind='body2'>
            {l('Error loading summary details, please try again.')}
          </Typography>
          <Button
            type='button'
            kind='outlined'
            isEnabled
            onClick={() => {
              setIsLoadingMc(true);
            }}>
            {l('Retry')}
          </Button>
        </Box>
      );
    }

    return (
      <Box className='Sod-ViolationsRemoveMc-bodyWrapper'>
        <InputField
          name='remove-mc--action-comment'
          kind='multiline'
          label={
            <Typography kind='label' htmlFor='remove-mc-action-comment'>
              {l('Comments')}
            </Typography>
          }
          tabIndex={0}
          value={commentValue}
          setValue={setCommentValue}
          placeholder={l('Provide a reason for your decision')}
          isRequired={!isSubmitButtonEnable}
          isCritical={isCommentCritical}
          setIsCritical={setIsCommentCritical}
          CriticalHelperText={
            <InlineMessage
              text={
                commentValue.trim().length < 1
                  ? l('This required field is incomplete')
                  : `Your comments must contain more than ${
                      MIN_COMMENT_LENGTH - 1
                    } characters.`
              }
              colorTheme='critical'
              size='small'
              leftIcon={<Icon kind='AlertCriticalFilled' />}
            />
          }
        />
      </Box>
    );
  };

  return (
    <ModalIsland
      isOpen={isOpen}
      onClose={handleModalClose}
      headerIcon={headerIcon}
      title={
        inSingleMc
          ? 'Remove this Migating Control?'
          : `Remove Mitigating Control${
              mitigatingControlCount && mitigatingControlCount > 1
                ? ` (${mitigatingControlCount})`
                : ''
            }?`
      }
      subtitle={
        inSingleMc
          ? l(
              'The violation will remain in a "Mitigated" status because at least one other mitigating control is still active.'
            )
          : l('The violation will be moved to the “Open” status.')
      }
      isCloseButtonVisible={!isDisabled}
      contentBody={renderBody()}
      FooterActionBarComp={
        <FooterActionBar size='large' isScrolled={sectionRefIsScrolled}>
          <div />
          <div className='Sod-ViolationsRemoveMc-footerActionButtons'>
            <Button
              type='button'
              kind='ghost'
              size='medium'
              isDisabled={isDisabled ?? isLoading}
              onClick={handleModalClose}>
              {l('Cancel')}
            </Button>
            <Button
              type='button'
              kind={isSubmitButtonEnable ? 'filled' : 'outlined'}
              size='medium'
              onClick={() => {
                if (isSubmitButtonEnable) {
                  handleSumbit(commentValue, {
                    mitigatingControlKeys:
                      mcKeys ?? mitigatingControlRows.map(({ id }) => id),
                    violationId: Number(sodKey),
                  });

                  return;
                }

                setIsCommentCritical(true);
              }}
              isDisabled={isDisabled && !isLoading}
              isLoading={isLoading}
              loaderText={l('Saving')}>
              {l('Save')}
            </Button>
          </div>
        </FooterActionBar>
      }
    />
  );
}

ViolationsRemoveMc.defaultProps = {
  isDisabled: false,
  mcKeys: null,
  mitigatingControlCount: undefined,
};

export default memo(ViolationsRemoveMc);
